import { Button, Flex, FormControl, FormLabel, HStack, Icon, IconButton, Input, Stack, Text } from '@chakra-ui/react';
import { ChangeEvent, useEffect, useState } from 'react';
import { FiEye, FiFile, FiTrash2, FiUploadCloud } from 'react-icons/fi';
import { formatFileSize } from '../../../../../utils/functions';
import MessageError from '../../../../../components/MessageError';

const DOCUMENT_LABELS = [
  'Documento de identificação',
  'Comprovante de doação de medula óssea',
  'Comprovante de cadastro no REDOME',
];

interface IProps {
  files: (File | undefined)[];
  hasError?: boolean;
  errorMessage?: string;
  onSelectFile: (index: number, file: File) => void;
  onRemoveFile: (index: number) => void;
}

interface ISelectedFileRowProps {
  file: File;
  onRemove: () => void;
  mt?: number;
}

const SelectedFileRow: React.FC<ISelectedFileRowProps> = ({ file, onRemove, mt }) => {
  const [fileUrl, setFileUrl] = useState('');

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setFileUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <Flex
      py={2}
      px={3}
      border="1px solid"
      borderColor="green.200"
      borderRadius="lg"
      bg="green.50"
      boxShadow="sm"
      mt={mt}
      minW={0}
      align="center"
      gap={3}
    >
      <Icon as={FiFile} boxSize={5} color="green.600" flexShrink={0} />
      <Flex minW={0} flex={1} align="center" gap={1}>
        <Text fontSize="sm" fontWeight="semibold" isTruncated title={file.name} minW={0} flexShrink={1}>
          {file.name}
        </Text>
        <Text fontSize="xs" color="gray.500" whiteSpace="nowrap" flexShrink={0}>
          ({formatFileSize(file.size)})
        </Text>
      </Flex>
      <HStack spacing={1} flexShrink={0}>
        <IconButton
          type="button"
          aria-label={`Visualizar ${file.name}`}
          title="Visualizar arquivo"
          icon={<FiEye />}
          variant="ghost"
          size="sm"
          colorScheme="blue"
          isDisabled={!fileUrl}
          onClick={() => {
            if (fileUrl) window.open(fileUrl, '_blank', 'noopener,noreferrer');
          }}
        />
        <IconButton
          type="button"
          aria-label={`Remover ${file.name}`}
          title="Remover arquivo"
          icon={<FiTrash2 />}
          variant="ghost"
          size="sm"
          colorScheme="red"
          onClick={onRemove}
        />
      </HStack>
    </Flex>
  );
};

const BoneMarrowUploadFields: React.FC<IProps> = ({ files, hasError, errorMessage, onSelectFile, onRemoveFile }) => {
  const handleFileChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    if (file) onSelectFile(index, file);
    event.currentTarget.value = '';
  };

  return (
    <Stack spacing={4}>
      {DOCUMENT_LABELS.map((label, index) => {
        const file = files[index];
        const isMissing = hasError && !file;
        const inputId = `bone-marrow-document-${index}`;

        return (
          <FormControl key={label} isRequired isInvalid={isMissing}>
            <FormLabel fontSize="sm" mb={2}>
              {label}
            </FormLabel>
            <Input
              id={inputId}
              display="none"
              onChange={(event) => handleFileChange(index, event)}
              type="file"
              multiple={false}
              accept=".pdf,.jpg,.jpeg,.png"
              aria-label={`Enviar ${label.toLowerCase()}`}
            />
            <Button
              as="label"
              htmlFor={inputId}
              size="sm"
              variant="outline"
              colorScheme="blue"
              leftIcon={<FiUploadCloud />}
              cursor="pointer"
              fontWeight="medium"
            >
              {file ? 'Substituir arquivo' : 'Selecionar arquivo'}
            </Button>
            {file && <SelectedFileRow mt={2} file={file} onRemove={() => onRemoveFile(index)} />}
            {isMissing && <Text color="red.500" fontSize="xs" mt={1}>Este documento é obrigatório.</Text>}
          </FormControl>
        );
      })}
      <Text fontSize="xs" color="gray.500">
        PDF, JPG ou PNG · até 2 MB por arquivo.
      </Text>
      {errorMessage && files.every(Boolean) && <MessageError>{errorMessage}</MessageError>}
    </Stack>
  );
};

export default BoneMarrowUploadFields;
